import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-09-10 17:22:11 +0200 (Thu, 10 Sep 2026) $
//  Author    : Albert Charlier, Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 165450 $
//  Purpose   : pdf viewer
// =============================================================================
// ---Third-party imports
import React, { memo } from 'react';
import { Header, Menu, Dimmer, Loader, Segment } from 'semantic-ui-react';
import { PDFDocument, PDFPage, PDFOutline, ReflexContainer, ReflexSplitter, ReflexElement } from 'uix/chunks';
// ---Framework imports
import log from 'uix/logging';
import connect from 'uix/connect';
import { isNil } from 'ldash';
import { AutoPortal, HeaderSegment } from 'uix/components';
import { prettyBytes } from 'uix/utils';
import { useState, useRef, useEffect, usePersistent } from 'uix/hooks';
// ---Local imports
import styles from './styles.css';
// ---Globals
class PromiseCancelledException extends Error {
    constructor(message, type){
        super(message, type);
        this.name = 'PromiseCancelledException';
        this.message = message;
        this.type = type;
    }
}
const makeCancellable = (promise)=>{
    let isCancelled = false;
    const wrappedPromise = new Promise((resolve, reject)=>{
        promise.then((...args)=>isCancelled ? reject(new PromiseCancelledException('Promise cancelled')) : resolve(...args), (error)=>isCancelled ? reject(new PromiseCancelledException('Promise cancelled')) : reject(error));
    });
    return {
        promise: wrappedPromise,
        cancel () {
            isCancelled = true;
        }
    };
};
const cancelRunningTask = (runningTask)=>{
    if (!runningTask || !runningTask.cancel) {
        return;
    }
    runningTask.cancel();
};
const isDefined = (variable)=>typeof variable !== 'undefined';
class Ref {
    toString() {
        let str = `${this.num}R`;
        if (this.gen !== 0) {
            str += this.gen;
        }
        return str;
    }
    constructor({ num, gen }){
        this.num = num;
        this.gen = gen;
    }
}
const OutlineItem = /*#__PURE__*/ memo(({ item, ...otherProps })=>{
    /* memoized outline entry with lazy destination/page lookups
   */ const cache = useRef({});
    const getDestination = async ()=>{
        const { pdf } = otherProps;
        if (!isDefined(cache.current.destination)) {
            if (typeof item.dest === 'string') {
                cache.current.destination = await pdf.getDestination(item.dest);
            } else {
                cache.current.destination = item.dest;
            }
        }
        return cache.current.destination;
    };
    const getPageIndex = async ()=>{
        const { pdf } = otherProps;
        if (!isDefined(cache.current.pageIndex)) {
            const destination = await getDestination();
            if (destination) {
                const [ref] = destination;
                cache.current.pageIndex = await pdf.getPageIndex(new Ref(ref));
            }
        }
        return cache.current.pageIndex;
    };
    const getPageNumber = async ()=>{
        if (!isDefined(cache.current.pageNumber)) {
            cache.current.pageNumber = await getPageIndex() + 1;
        }
        return cache.current.pageNumber;
    };
    const onClickItem = async (event)=>{
        const { onClick } = otherProps;
        event.preventDefault();
        const pageIndex = await getPageIndex();
        const pageNumber = await getPageNumber();
        if (onClick) {
            onClick({
                pageIndex,
                pageNumber
            });
        }
    };
    const renderSubitems = ()=>{
        if (!item.items || !item.items.length) {
            return null;
        }
        const { items: subitems } = item;
        return /*#__PURE__*/ _jsx("ul", {
            children: subitems.map((subitem, subitemIndex)=>/*#__PURE__*/ _jsx(OutlineItem, {
                    item: subitem,
                    ...otherProps
                }, typeof subitem.destination === 'string' ? subitem.destination : subitemIndex))
        });
    };
    /* eslint-disable jsx-a11y/anchor-is-valid */ return /*#__PURE__*/ _jsxs("li", {
        children: [
            /*#__PURE__*/ _jsx("a", {
                href: "#",
                onClick: onClickItem,
                children: item.title
            }),
            renderSubitems()
        ]
    });
});
const MIN_OUTLINE = 2;
const _PDF = ({ apiurl, otid, pageIndex, showOutline, rotate, onLoadProgress, onLoadSuccess: onLoadSuccessProp, onOutlineLoaded, onOutlineItemClicked })=>{
    const [pdf, setPdf] = useState(null);
    const [outline, setOutline] = useState(null);
    const runningTask = useRef(null);
    const loadOutline = async (pdf)=>{
        setOutline((prev)=>prev ? null : prev);
        try {
            const cancellable = makeCancellable(pdf.getOutline());
            runningTask.current = cancellable;
            const outline = await cancellable.promise;
            setOutline(outline);
            if (onOutlineLoaded) {
                onOutlineLoaded(outline);
            }
        } catch (error) {
            log.error(error);
            setOutline(null);
            if (onOutlineLoaded) {
                onOutlineLoaded(null);
            }
        }
    };
    const onLoadSuccess = (pdf)=>{
        //~ log('loaded PDF with %o pages %O', pdf.numPages, pdf)
        setPdf(pdf);
        loadOutline(pdf);
        if (onLoadSuccessProp) {
            onLoadSuccessProp(pdf);
        }
    };
    useEffect(()=>{
        return ()=>{
            cancelRunningTask(runningTask.current);
        };
    }, []);
    const renderOutline = ()=>/*#__PURE__*/ _jsx("ul", {
            className: styles.outline,
            children: outline.map((item, itemIndex)=>/*#__PURE__*/ _jsx(OutlineItem, {
                    item: item,
                    pdf: pdf,
                    onClick: onOutlineItemClicked
                }, typeof item.destination === 'string' ? item.destination : itemIndex))
        });
    let url = `${apiurl}/download/pdf/${otid}`;
    let loader = /*#__PURE__*/ _jsx(Dimmer, {
        active: true,
        inverted: true,
        children: /*#__PURE__*/ _jsx(Loader, {
            inverted: true,
            content: "Loading..."
        })
    });
    let outlineActive = showOutline && outline;
    return /*#__PURE__*/ _jsx("div", {
        style: {
            display: 'flex',
            flex: 1
        },
        children: /*#__PURE__*/ _jsxs(ReflexContainer, {
            orientation: "vertical",
            children: [
                outlineActive ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(ReflexElement, {
                            flex: "0.3",
                            children: renderOutline()
                        }, "outline"),
                        /*#__PURE__*/ _jsx(ReflexSplitter, {
                            className: styles.splitter
                        })
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(ReflexElement, {
                    flex: outlineActive ? 0.7 : 1.0,
                    children: /*#__PURE__*/ _jsx(PDFDocument, {
                        renderMode: "canvas",
                        file: url,
                        onLoadSuccess: onLoadSuccess,
                        onLoadProgress: onLoadProgress,
                        loading: loader,
                        children: /*#__PURE__*/ _jsx(PDFPage, {
                            pageIndex: pageIndex,
                            loading: "",
                            rotate: rotate
                        })
                    })
                }, "page")
            ]
        })
    });
};
const PDF = connect('PDF', 'config.apiurl', _PDF);
export const PDFViewer = ({ request })=>{
    /* render PDF documents
   */ let [state, _setState] = useState({
        numPages: -1,
        showOutline: true,
        rotate: 0,
        scale: 0.5,
        loadingSize: {
            loaded: 0,
            total: 0
        }
    });
    let [pageIndexes, setPageIndexes] = usePersistent('pageIndexes', {});
    let setState = (data)=>_setState((state)=>({
                ...state,
                ...data
            }));
    //~ static partStyle = {display: 'flex'}
    let setPageIndex = (otid, idx)=>{
        setPageIndexes({
            ...pageIndexes,
            [otid]: idx
        });
    };
    let onLoadSuccess = (pdf)=>{
        setState({
            numPages: pdf.numPages
        });
    };
    let onOutlineLoaded = (outline)=>{
        setState({
            outline
        });
    };
    let onLoadProgress = ({ loaded, total })=>{
        //~ log.info(loaded, total)
        setState({
            loadingSize: {
                loaded,
                total
            }
        });
    };
    let onOutlineItemClicked = ({ pageNumber })=>{
        setPageIndex(request.id, pageNumber - 1);
    };
    let { numPages, showOutline, rotate, outline, loadingSize } = state;
    let pageIndex = pageIndexes[request.id] || 0;
    let outlineDisabled = !outline || outline.length <= MIN_OUTLINE;
    let menu = /*#__PURE__*/ _jsxs(Menu, {
        children: [
            /*#__PURE__*/ _jsx(Menu.Item, {
                title: "Show Outline",
                active: showOutline,
                disabled: outlineDisabled,
                icon: "content",
                onClick: ()=>setState({
                        showOutline: !showOutline
                    })
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                title: "Rotate Left",
                icon: "undo",
                onClick: ()=>setState({
                        rotate: (rotate + 270) % 360
                    })
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                title: "Rotate Right",
                icon: "redo",
                onClick: ()=>setState({
                        rotate: (rotate + 90) % 360
                    })
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                icon: "left arrow",
                title: "Next Page",
                disabled: pageIndex == 0,
                onClick: ()=>setPageIndex(request.id, pageIndex - 1)
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                content: `${pageIndex + 1}/${numPages}`
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                icon: "right arrow",
                title: "Next Page",
                disabled: pageIndex >= numPages - 1,
                onClick: ()=>setPageIndex(request.id, pageIndex + 1)
            })
        ]
    });
    showOutline = showOutline && !outlineDisabled;
    let loading = loadingSize.total > loadingSize.loaded;
    let subheader = `${prettyBytes(loadingSize.total)}`;
    if (loading) {
        subheader = `${subheader} (${prettyBytes(loadingSize.loaded)})`;
    }
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        header: "PDF",
        flex: true,
        overflowInner: true,
        subheader: subheader,
        loading: loading,
        icon: "file pdf outline",
        menu: menu,
        children: /*#__PURE__*/ _jsx(PDF, {
            otid: request.id,
            showOutline: showOutline,
            pageIndex: pageIndexes[request.id] || 0,
            onOutlineItemClicked: onOutlineItemClicked,
            onOutlineLoaded: onOutlineLoaded,
            onLoadSuccess: onLoadSuccess,
            onLoadProgress: onLoadProgress,
            rotate: rotate
        })
    });
};
